import { Col, DatePicker, Form, Row } from 'antd';
import React from 'react';
import { CurrencyInput, Modal } from '@common/components';
import { useMutation } from '@common/hooks';
import { dollarsToCents } from '@common/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  UpdateInvoiceInstallmentMutation,
  InvoiceInstallmentFragmentType,
  UpdateInvoiceInstallmentResponse,
  UpdateInvoiceInstallmentArgs,
} from '@pay-bill/graphql';
import { US_DATE_FORMAT } from 'works/legacy/lib/date-helper';
import { Store } from 'antd/lib/form/interface';
import moment from 'moment';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';

export const EditInstallmentModal = ({
  visible,
  onAction,
  installment,
  remainingInstallmentBalance,
}: {
  visible: boolean;
  onAction: () => void;
  installment: InvoiceInstallmentFragmentType;
  remainingInstallmentBalance: number;
}) => {
  const [form] = Form.useForm();

  const [performUpdate, { loading }] = useMutation<
    UpdateInvoiceInstallmentResponse,
    UpdateInvoiceInstallmentArgs
  >(UpdateInvoiceInstallmentMutation, {
    onCompleted: (data) => {
      if (data?.updateInvoiceInstallment?.errors) {
        createMessage('error', 'Request failed. Please try again.');
        addErrorsToForm(form, data.updateInvoiceInstallment.errors);
      } else {
        createMessage('success', `Payment successfully saved`);
        onAction();
      }
    },
    onError: () => {
      createMessage('error', 'Request failed. Please try again.');
    },
    refetchQueries: ['GetWorksInvoice'],
  });

  const onEdit = (values: Store) => {
    performUpdate({
      variables: {
        invoiceInstallmentId: installment.id,
        input: {
          paymentAmountCents: dollarsToCents(values.paymentAmount),
          paidAt: values.paidAt,
        },
      },
    });
  };

  const initialValues = {
    paidAt: moment(installment.paidAt),
    paymentAmount: installment.paymentAmountCents / 100,
  };

  const paymentAmountMaxValue =
    (remainingInstallmentBalance + installment.paymentAmountCents) / 100;

  return (
    <Modal
      visible={visible}
      cancelText="Cancel"
      okText="Save"
      onCancel={onAction}
      title="Edit Payment"
      confirmLoading={loading}
      onOk={form.submit}
      className="edit-installment-modal"
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onEdit}
        initialValues={initialValues}
      >
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item
              name="paymentAmount"
              label="Amount Paid"
              rules={[
                { required: true, message: 'Please select the amount paid' },
                {
                  max: paymentAmountMaxValue,
                  type: 'number',
                  message: 'Amount Paid is more than the remaining balance.',
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <CurrencyInput size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="paidAt"
              label="Date Paid"
              rules={[
                { required: true, message: 'Please select the date paid' },
              ]}
              style={{ marginBottom: 0 }}
            >
              <DatePicker
                format={US_DATE_FORMAT}
                size="large"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
